import React from 'react'
import { useState } from 'react'
import { getSelectedGeos } from '@utils/GeoUtils'
import modules from '@modules'
import { Typography, Tag, Button } from 'antd'
import useActions from '@hooks/useActions'

import './EpsilonSelectedFilterBar.less'
import useAccount from '@hooks/useAccount'
import { getSelectedCriticalMoments } from '@utils/CriticalMomentUtils'
import useClientConfig from 'config/clients/useClientConfig'
import { selectedLogicals } from '@utils/LogicalUtils'
import useClientTranslation from 'config/clients/useClientTranslation'
import { selectedChannels } from '@utils/ChannelUtils'

type PropsType = {
  filtersData: Record<string, any>
  filters: FiltersUI
}


/**
 * FilterBar
 */
export const EpsilonSelectedFilterBar = ({ filters, filtersData, }: PropsType): JSX.Element => {
  const dispatchers = useActions(modules.actions.appData, [])
  const { userProfiles, criticalMoments } = useAccount()
  const [rerender, setRerender] = useState<number>(1)
  const { translate } = useClientTranslation()

  const {
    includesFilterGeo,
    includesFilterCriticalMoment,
    includesFilterChannel,
    getGeosRootId,
    getLogicalsRootId,
    getFilterCriticalMomentDefault,
    getCriticalMomentsRootId,
    includesFilterLogical,
    supportsInternationalization
  } = useClientConfig()

  const geos = getSelectedGeos(filtersData.geographicDistributions, filters.geographicDistributions)
  const logicals = selectedLogicals(filtersData.logicalDistributions, filters.logicalDistributions)
  const cms = getSelectedCriticalMoments(filtersData.criticalMoments, filters.criticalMoments)
  const channels = selectedChannels(filtersData.channels, filters.channels)

  //console.log('Rendering selected filters bar')
  //console.log(cms)
  //console.log(criticalMoments)
  //console.log(getGeosRootId())
  //console.log(geosFilters)
  //console.log(cmsFilters)

  React.useEffect(() => {
    setRerender(rerender + 1)
  }, [filters,])

  const handleDeleteGeo = (geo: number) => {
    let selectedGeos = filters.geographicDistributions
      .filter(item => item !== geo)

    if (selectedGeos.length === 0) {
      selectedGeos = [filtersData.geographicDistributions[0].id]
    }

    dispatchers.setFilters({
      ...filters,
      geographicDistributions: selectedGeos
    })
  }

  const handleDeleteLogical = (logical: number) => {
    let selectedLogicals = filters.logicalDistributions
      .filter(item => item !== logical)

    if (selectedLogicals.length === 0) {
      selectedLogicals = [filtersData.logicalDistributions[0].id]
    }

    dispatchers.setFilters({
      ...filters,
      logicalDistributions: selectedLogicals
    })
  }

  const handleDeleteChannel = (channel: number) => {
    let selectedChannels: number[] | undefined = filters.channels
      .filter(item => item !== channel)

    if (selectedChannels.length === 0) {
      selectedChannels = undefined
    }

    dispatchers.setFilters({
      ...filters,
      channels: selectedChannels
    })
  }

  const handleDeleteCriticalMoment = (cm: number) => {
    //console.log('Elimino selected critical Moment ' + cm)
    let selectedCms = filters.criticalMoments
      .filter(item => item !== cm)

    //console.log(selectedCms)
    //console.log(getFilterCriticalMomentDefault(filtersData, userProfiles))

    if (selectedCms.length === 0) {
      //selectedCms = [filtersData.criticalMoments[0].id]
      selectedCms = [getFilterCriticalMomentDefault(filtersData, userProfiles)]
    }

    dispatchers.setFilters({
      ...filters,
      criticalMoments: selectedCms,
      responseValues: []
    })
  }

  const geosFilters = includesFilterGeo(userProfiles)
    && geos.some(geo => geo.id !== getGeosRootId())

  const logicalsFilters = includesFilterLogical(userProfiles)
    && logicals.some(logical => logical.id !== getLogicalsRootId())

  const cmsFilters = includesFilterCriticalMoment(userProfiles)
    && cms.some(cm => cm.id !== getCriticalMomentsRootId())

  const channelsFilters = includesFilterChannel(userProfiles)
    && channels.some(ch => ch.id !== undefined)

  const showBar = geosFilters || logicalsFilters || cmsFilters || channelsFilters

  const normalizeName = (name: string) => {
    return name?.replaceAll(/[-_]/g, " ")
  }

  return (
    <div className='selected-filter-bar-component'>
      {showBar && (
        <Typography className='title-selected-filters-bar-component'>{translate('general.filters')}:</Typography>
      )}
      {geosFilters && geos
        .filter(geo => geo.id !== getGeosRootId())
        .map((geo: any, idx: number) => {
          return (
            <Tag
              key={'selected-geo-' + geo.id}
              closable
              onClose={() => handleDeleteGeo(geo.id)}
              className='tag-style-filters-bar-component'
            >{geo.description}</Tag>
          )
        })}
      {logicalsFilters && logicals
        .filter(logical => logical.id !== getGeosRootId())
        .map((logical: any, idx: number) => {
          return (
            <Tag
              key={'selected-logical-' + logical.id}
              closable
              onClose={() => handleDeleteLogical(logical.id)}
              className='tag-style-filters-bar-component'
            >{logical.description}</Tag>
          )
        })}
      {cmsFilters && cms
        .filter(cm => cm.id !== getCriticalMomentsRootId())
        .map((cm: any, idx: number) => {
          return (
            <Tag
              key={'selected-cm-' + cm.id}
              closable
              onClose={() => handleDeleteCriticalMoment(cm.id)}
              className='tag-style-filters-bar-component'
            >
              { supportsInternationalization()
                && cm.id
                && translate('critical_moments.byId.' + cm.id) ?
                    translate('critical_moments.byId.' + cm.id) : 
                    translate('critical_moments.byId.' + cm.id, 'N/S') != 'N/S' ? 
                        translate('critical_moments.byId.' + cm.id) : 
                            (cm?.description?.length > 30 ? 
                                normalizeName(cm?.name) : cm?.description)}
            </Tag>
          )
        })}
      {channelsFilters && channels
        .filter(channel => channel.id !== undefined)
        .map((channel: any, idx: number) => {
          return (
            <Tag
              key={'selected-channel-' + channel.id}
              closable
              onClose={() => handleDeleteChannel(channel.id)}
              className='tag-style-filters-bar-component'
            >{channel.description}</Tag>
          )
        })}
    </div>
  )
}
